// HAND-CURATED allowlist of Evaluar syllable keys that have a pre-recorded MP3 on the CDN.
// (It was previously labelled AUTO-GENERATED, but the generator it named has never existed in
// this repo — it is maintained by hand, so it is documented that way.)
//
// Add a key ONLY once its asset already serves HTTP 200. A Howler load error plays as SILENCE
// that the app cannot catch, so an allowlisted-but-missing asset is strictly worse than the TTS
// fallback it was meant to replace — hence an allowlist rather than 404-and-recover.
//
// Verify (read-only, no credentials needed) before and after any edit:
//   node scripts/verify-spanish-syllable-assets.mjs
// It HEADs every key below and fails on anything but 200.
//
// Source: assets.app.amiralearning.com/syllables/spanish/<key>.mp3. Whole set (DECLARED_KEY_COUNT
// below) verified HTTP 200 on 2026-08-01 via the script above. The served path is
// locale-namespaced (syllables/spanish/) to match the existing phonemes/spanish/ and
// audio/spanish/ conventions and avoid cross-locale key collisions.
//
// Key rule (see normalizeSyllableKey): stress accents stripped (á→a), EXCEPT an accented í/ú
// beside a strong vowel, which marks hiatus and is doubled (oí→"oii"); the phonemically
// distinct diacritics encoded as collision-free digraphs — ñ→"nn", ü→"uu" (ña→"nna",
// güi→"guui"). Keys NOT in this set fall back to TTS.
//
// Coverage: as of 2026-08-01 every syllable the item bank requests has a live asset
// (ASSMNT-1951, Done). Point-in-time — new content can reintroduce a gap; that is what
// the verify script is for.
// Batch 260723 added: ab ac ad af ger gim gip gir gis jen jes jis jon jor jos jun juz nar rue yac yak yang.
// Added 260728: nis (the tenis syllable, the last uncovered sound).

/**
 * How many keys this file is asserted to contain, and
 * therefore how many CDN assets must serve HTTP 200. The single source of truth for that number:
 * syllableToAudio.test.js asserts the collections match it (fails in CI, no network needed) and
 * scripts/verify-spanish-syllable-assets.mjs reads it to confirm its own parse before hitting the
 * network. Bumping this is the deliberate second half of adding a key — and the prompt to re-run
 * the verify script and update the header's verification date.
 */
export const DECLARED_KEY_COUNT = 392;

// Verified-present syllable keys (accent-stripped, ñ/ü-encoded ASCII).
// The literal list is exported alongside the Set because `new Set([...])` silently swallows a
// duplicate entry — the Set's size cannot reveal one. syllableToAudio.test.js compares the two,
// so a key pasted in twice fails CI instead of quietly consuming one of DECLARED_KEY_COUNT's slots.
export const SYLLABLE_KEY_LIST = [
  "a", "ab", "ac", "ad", "af", "ai", "al", "am", "an", "ar", "as", "au", "ba", "bai", "bal",
  "ban", "bar", "bas", "be", "ber", "bier", "bir", "bla", "ble", "blo", "blu", "bo", "bol", "bon",
  "bor", "bos", "bra", "bre", "bri", "briz", "bro", "bru", "bu", "bue", "bus", "ca", "cam", "can",
  "car", "cas", "ce", "cen", "cer", "ces", "cha", "char", "che", "chi", "cho", "chu", "ci", "cia",
  "cie", "ciem", "cin", "cio", "cion", "cir", "cla", "cle", "clo", "clu", "co", "col", "con", "cor",
  "cra", "cre", "cri", "cru", "cu", "cua", "cue", "cuen", "cuer", "da", "dad", "dal", "dar", "das",
  "de", "del", "den", "der", "des", "di", "dia", "diar", "din", "dio", "dir", "do", "doc", "don",
  "dor", "dra", "dre", "dri", "du", "dul", "e", "ea", "ei", "en", "es", "fa", "fan", "fe",
  "fi", "fies", "fil", "fin", "fir", "fla", "flau", "fle", "flo", "flor", "fo", "fom", "fon", "for",
  "fra", "fram", "fre", "fri", "fru", "fuer", "ga", "gar", "gas", "ge", "gen", "ger", "gi", "gim",
  "gip", "gir", "gis", "gla", "glo", "go", "gra", "gran", "gre", "gri", "gu", "gua", "gual", "guan",
  "gue", "gui", "guue", "guui", "ha", "hi", "ho", "hom", "hu", "hue", "huer", "i", "ia", "ie",
  "in", "io", "is", "iu", "iz", "ja", "jal", "jar", "je", "jen", "jer", "jes", "ji", "jin",
  "jis", "jo", "jon", "jor", "jos", "ju", "jun", "juz", "la", "lan", "lar", "las", "le", "lec",
  "len", "li", "lia", "lim", "lin", "lis", "liz", "lla", "llan", "llar", "lle", "llo", "llu", "lo",
  "loj", "lom", "lon", "lor", "los", "lu", "lum", "ma", "mal", "man", "mar", "mas", "me", "men",
  "mer", "mi", "mio", "mir", "mo", "mon", "mos", "mu", "mue", "mun", "na", "nar", "nau", "ne",
  "nel", "ner", "nes", "ni", "nin", "nis", "nna", "nnar", "nne", "nno", "no", "nu", "nue", "nun", "o",
  "oi", "oii", "on", "or", "pa", "pan", "par", "pas", "pe", "pei", "pes", "pi", "pie", "pier", "pies",
  "pin", "pio", "pis", "piz", "pla", "plan", "plo", "plu", "po", "pol", "pon", "por", "pos", "pre",
  "pri", "pro", "pu", "puen", "pul", "pun", "que", "ques", "qui", "ra", "ran", "rar", "ras", "re",
  "rei", "res", "ri", "rio", "ris", "riz", "ro", "rom", "ron", "roz", "rra", "rre", "rri", "rril",
  "rro", "rue", "sa", "sal", "san", "sar", "sas", "se", "sec", "sel", "si", "so", "som", "sor",
  "su", "ta", "tad", "tal", "tam", "tan", "tar", "tas", "te", "tel", "tem", "ten", "ter", "tes",
  "ti", "tie", "tien", "tim", "tin", "tis", "to", "ton", "tor", "tos", "tra", "trac", "tram", "tre",
  "tri", "tris", "tro", "trom", "tron", "tu", "u", "ua", "ue", "ui", "va", "ve", "ven", "ver",
  "ves", "vi", "via", "vie", "vien", "vio", "vion", "vis", "vo", "vol", "ya", "yac", "yak", "yang",
  "ye", "yer", "yo", "yon", "za", "zar", "zas", "zo", "zon", "zu", "zul",
];

// Verified-present syllable keys (accent-stripped, ñ/ü-encoded ASCII).
export const PRESENT_SYLLABLE_KEYS = new Set(SYLLABLE_KEY_LIST);
